import { apiClient } from '@/lib/api-client';
import { API_CONFIG } from '@/config/api';
import { Product, ProductsResponse } from '@/types';

/**
 * Product query parameters
 */
export interface ProductQueryParams {
  search?: string;
  per_page?: number;
  page?: number;
  pagination?: boolean;
  sort_by?: 'created_at' | 'price' | 'name' | 'total_orders';
  sort_order?: 'asc' | 'desc';
  category_id?: string | number;
}

/**
 * Service for handling product-related API calls
 */
export class ProductService {
  /**
   * Fetch all products with optional filters
   * @param params - Optional query parameters
   * @returns Promise with products data
   */
  static async getProducts(params?: ProductQueryParams): Promise<ProductsResponse['data']> {
    console.log('ProductService.getProducts called with params:', params);
    const queryParams = new URLSearchParams();
    
    // Set default values
    const defaultParams: ProductQueryParams = {
      search: '',
      per_page: 20, // Reduced from 50 to 20
      page: 1,
      pagination: true,
      sort_by: 'created_at',
      sort_order: 'asc',
      category_id: '',
      ...params,
    };

    console.log('ProductService.getProducts merged params:', defaultParams);

    // Build query string - only include non-empty values
    Object.entries(defaultParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        queryParams.append(key, String(value));
      }
    });

    const endpoint = `${API_CONFIG.ENDPOINTS.PRODUCTS}?${queryParams.toString()}`;
    console.log('ProductService.getProducts - Calling API:', endpoint);
    const response = await apiClient.get<ProductsResponse>(endpoint);

    if (!response.status) {
      throw new Error(response.message || 'Failed to fetch products');
    }

    // Transform the new API response to match expected format
    const data = response.data;
    
    // Create pagination object from the new response structure
    const page = defaultParams.page || 1;
    const perPage = defaultParams.per_page || 20;
    const total = data.total;
    const totalPages = Math.ceil(total / perPage);
    
    return {
      products: data.products,
      total: data.total,
      from: data.from,
      size: data.size,
      category_ids: data.category_ids,
      search_query: data.search_query,
      used_default_categories: data.used_default_categories,
      // Add backward compatibility fields
      pagination: {
        current_page: page,
        per_page: perPage,
        total: total,
        total_pages: totalPages,
        has_next_page: page < totalPages,
        has_prev_page: page > 1,
        next_page: page < totalPages ? page + 1 : null,
        prev_page: page > 1 ? page - 1 : null,
        from: data.from,
        to: Math.min(data.from + data.size, total),
      },
      filters: {
        search: defaultParams.search || '',
        sort_by: defaultParams.sort_by || 'created_at',
        sort_order: defaultParams.sort_order || 'asc',
      },
      category: null,
      total_mini_app_categories: 0,
    };
  }

  /**
   * Search products by query
   * @param query - Search query string
   * @param params - Additional query parameters
   * @returns Promise with products data
   */
  static async searchProducts(
    query: string,
    params?: Omit<ProductQueryParams, 'search'>
  ): Promise<ProductsResponse['data']> {
    return this.getProducts({ ...params, search: query });
  }

  /**
   * Get products by category
   * @param categoryId - Category ID
   * @param params - Additional query parameters
   * @returns Promise with products data
   */
  static async getProductsByCategory(
    categoryId: string | number,
    params?: Omit<ProductQueryParams, 'category_id'>
  ): Promise<ProductsResponse['data']> {
    return this.getProducts({ ...params, category_id: categoryId });
  }

  /**
   * Get paginated products
   * @param page - Page number
   * @param perPage - Items per page
   * @param params - Additional query parameters
   * @returns Promise with products data
   */
  static async getPaginatedProducts(
    page: number = 1,
    perPage: number = 50,
    params?: Omit<ProductQueryParams, 'page' | 'per_page'>
  ): Promise<ProductsResponse['data']> {
    return this.getProducts({ ...params, page, per_page: perPage });
  }
}



// Export default instance for convenience
export const productService = ProductService;
